/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * This browser's device before an owner write (backlog R4): the note band's publish and the
 * wall's report both need one. The token itself stays in the httpOnly cookie (#41) — the
 * browser only ever sees the marker cookie and `{ profile_id, handle }`.
 *
 * Not to be confused with the route `pages/api/owner-notes/device.ts` this calls, which sets
 * the httpOnly cookie server-side.
 */
import type { OwnerDeviceMinted } from 'lib/dealhunt/types'
import { OwnerApiError, ownerApi } from './api'
import { hasDeviceMarker, notifyDeviceChanged } from './declared'

/**
 * Mint this browser's device with a fresh bot-check token (the route refreshes a live one
 * instead of minting a second profile), then tell every own-state reader on the page
 * (`useDeviceEpoch`) — a device that appeared in place is theirs to re-read.
 */
export async function mintDevice(botToken: () => Promise<string>): Promise<OwnerDeviceMinted> {
  const minted = await ownerApi.mintDevice(await botToken())
  notifyDeviceChanged()
  return minted
}

/** Mint only when no marker cookie says this browser already has a device. */
export async function ensureDevice(botToken: () => Promise<string>): Promise<void> {
  if (hasDeviceMarker()) {
    return
  }
  await mintDevice(botToken)
}

let minting: Promise<unknown> | null = null

/**
 * The page's ONE in-flight device mint, for every signed-out write that may need a device (the
 * feed's vote, `VoteControl`, and its Ask, `AskOwner`): a feed has many rows, and two signed-out
 * taps while the first bot check runs would each mint (the route only refreshes a device whose
 * cookie is already set) — the last cookie wins and the other write sits under a device the
 * browser no longer holds. Every caller awaits the same mint; `retry` (after DealHunt answered
 * `device_unknown`) always mints. Constraint 625cfc2c: never a second module-level mint.
 */
export function mintOnce(botToken: () => Promise<string>, retry: boolean): Promise<unknown> {
  if (!minting) {
    minting = (retry ? mintDevice(botToken) : ensureDevice(botToken)).finally(() => {
      minting = null
    })
  }
  return minting
}

/** DealHunt forgot this browser's device (or never minted it): mint once and send again. */
const DEVICE_RETRY_CODES: ReadonlySet<string> = new Set(['device_unknown', 'device_required'])

/**
 * A signed-out write under the device: ensure the page's one mint (`mintOnce`), send, and on
 * `device_unknown` / `device_required` mint again and resend once — the note band's recovery,
 * shared by the feed's vote and Ask.
 */
export async function sendAsDevice<T>(
  botToken: () => Promise<string>,
  send: () => Promise<T>
): Promise<T> {
  await mintOnce(botToken, false)
  try {
    return await send()
  } catch (e) {
    if (e instanceof OwnerApiError && DEVICE_RETRY_CODES.has(e.code)) {
      await mintOnce(botToken, true)
      return send()
    }
    throw e
  }
}
